import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

interface Barber {
    id: string;
    name: string;
    availability: {
        [key: string]: string[];
    };
}

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ManageTimings: React.FC = () => {
    const [barbers, setBarbers] = useState<Barber[]>([]);
    const [selectedBarber, setSelectedBarber] = useState<string>('');
    const [selectedDay, setSelectedDay] = useState<string>('');
    const [newTime, setNewTime] = useState<string>('');

    useEffect(() => {
        const fetchBarbers = async () => {
            const querySnapshot = await getDocs(collection(db, 'barbers'));
            const barbersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Barber[];
            setBarbers(barbersData);
        };

        fetchBarbers();
    }, []);

    const handleUpdate = async (id: string, day: string, time: string) => {
        if (!time) return; // Prevent adding empty time
        const barber = barbers.find(b => b.id === id);
        if (barber) {
            const updatedAvailability = {
                ...barber.availability,
                [day]: [...(barber.availability[day] || []), time].sort()
            };
            await updateDoc(doc(db, 'barbers', id), { availability: updatedAvailability });
            setBarbers(barbers.map(b => (b.id === id ? { ...b, availability: updatedAvailability } : b)));
            // Clear the form fields
            setSelectedDay('');
            setNewTime('');
        }
    };

    const handleDeleteTime = async (id: string, day: string, time: string) => {
        const barber = barbers.find(b => b.id === id);
        if (barber) {
            const updatedAvailability = {
                ...barber.availability,
                [day]: barber.availability[day].filter(t => t !== time)
            };
            await updateDoc(doc(db, 'barbers', id), { availability: updatedAvailability });
            setBarbers(barbers.map(b => (b.id === id ? { ...b, availability: updatedAvailability } : b)));
        }
    };

    return (
        <div className="p-4 md:p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold mb-6">Manage Timings</h2>
            <div className="mb-6">
                <select
                    value={selectedBarber}
                    onChange={(e) => setSelectedBarber(e.target.value)}
                    className="border p-2 mb-2 w-full rounded-md"
                >
                    <option value="">Select Barber</option>
                    {barbers.map(barber => (
                        <option key={barber.id} value={barber.id}>{barber.name}</option>
                    ))}
                </select>
                <select
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    className="border p-2 mb-2 w-full rounded-md"
                >
                    <option value="">Select Day</option>
                    {daysOfWeek.map(day => (
                        <option key={day} value={day}>{day}</option>
                    ))}
                </select>
                <input
                    type="time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="border p-2 mb-4 w-full rounded-md"
                />
                <button
                    onClick={() => handleUpdate(selectedBarber, selectedDay, newTime)}
                    className="bg-blue-500 text-white p-2 w-full rounded-md hover:bg-blue-600 transition duration-300"
                >
                    Add Time
                </button>
            </div>
            <ul className="space-y-4">
                {barbers.map(barber => (
                    <li key={barber.id} className="p-4 bg-gray-100 rounded-lg shadow-md">
                        <div className="text-lg font-semibold mb-2">{barber.name}</div>
                        <ul className="space-y-2">
                            {daysOfWeek.map(day => (
                                barber.availability[day] && barber.availability[day].length > 0 && (
                                    <li key={day} className="flex flex-col md:flex-row justify-between items-start md:items-center">
                                        <div className="mb-2 md:mb-0">
                                            <strong>{day}:</strong> {barber.availability[day].sort().join(', ')}
                                        </div>
                                        <div className="flex flex-wrap space-x-2">
                                            {barber.availability[day].sort().map(time => (
                                                <button
                                                    key={time}
                                                    onClick={() => handleDeleteTime(barber.id, day, time)}
                                                    className="text-red-500 hover:text-red-700 transition duration-300"
                                                >
                                                    {time} ✕
                                                </button>
                                            ))}
                                        </div>
                                    </li>
                                )
                            ))}
                        </ul>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ManageTimings;